import React from "react";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { usePhysicsStore } from "../../../stores/physicsStore";
import { useObjectRegistry } from "../../../runtime/objectRegistry";

const EMPTY_BODY_NAMES = Object.freeze([]);

const areStringArraysEqual = (prev, next) => {
  if (prev === next) return true;
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  for (let index = 0; index < prev.length; index += 1) {
    if (prev[index] !== next[index]) return false;
  }
  return true;
};

/**
 * Only mounted while mapping debug is expanded. Tracks physics body names without repainting for
 * every pose payload, so the main panel does not repaint continuously during simulation.
 */
export default function PhysicsPanelMappingDebugExpanded() {
  const physicsBodyNames = useStoreWithEqualityFn(
    usePhysicsStore,
    (s) => s.latestPoses?.bodyNames || EMPTY_BODY_NAMES,
    areStringArraysEqual
  );
  const sceneObjects = useObjectRegistry() || {};
  const debugInfo = usePhysicsStore((s) => s.debugInfo);
  const requestDebugDump = usePhysicsStore((s) => s.actions.requestDebugDump);

  return (
    <div
      className="px-3 py-2 text-xs text-gray-200 space-y-2"
      style={{ maxHeight: 180, overflow: "auto", borderTop: "1px solid #374151" }}
    >
      <div className="flex items-center justify-between">
        <div className="text-gray-400">Worker debug</div>
        <button
          className="px-2 py-0.5 rounded border border-gray-600 text-gray-200 hover:bg-gray-700"
          onClick={() => requestDebugDump()}
        >
          Dump
        </button>
      </div>
      {debugInfo && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-gray-400" style={{ marginBottom: 4 }}>
              State
            </div>
            <pre
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >{`running: ${String(debugInfo.running)}\npaused: ${String(debugInfo.paused)}\nnu: ${String(debugInfo.nu)}\nctrl.length: ${String(debugInfo.ctrlLen)}\nctrlPreview: ${Array.isArray(debugInfo.ctrlPreview) ? debugInfo.ctrlPreview.join(", ") : String(debugInfo.ctrlPreview)}\nqposLen: ${String(debugInfo.qposLen)}\nqposPreview: ${Array.isArray(debugInfo.qposPreview) ? debugInfo.qposPreview.join(", ") : String(debugInfo.qposPreview)}`}</pre>
          </div>
          <div>
            <div className="text-gray-400" style={{ marginBottom: 4 }}>
              Names
            </div>
            <pre
              style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >{`bodyNamesLen: ${String(debugInfo.bodyNamesLen)}\nxmlParsedBodyNamesLen: ${String(debugInfo.xmlParsedBodyNamesLen)}\nbodyNames (preview): ${(debugInfo.bodyNames || []).join(", ")}`}</pre>
          </div>
        </div>
      )}
      {debugInfo?.lastControlsSummary && (
        <div>
          <div className="text-gray-400" style={{ marginBottom: 4 }}>
            Last controls received
          </div>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {JSON.stringify(debugInfo.lastControlsSummary, null, 2)}
          </pre>
        </div>
      )}
      <div className="flex gap-4">
        <div style={{ flex: 1 }}>
          <div className="text-gray-400" style={{ marginBottom: 4 }}>
            Scene object keys ({Object.keys(sceneObjects).length})
          </div>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {Object.keys(sceneObjects).sort().join("\n")}
          </pre>
        </div>
        <div style={{ flex: 1 }}>
          <div className="text-gray-400" style={{ marginBottom: 4 }}>
            Physics body names ({physicsBodyNames.length})
          </div>
          <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {physicsBodyNames.join("\n")}
          </pre>
        </div>
      </div>
      {(() => {
        try {
          const sceneKeys = new Set(Object.keys(sceneObjects || {}));
          const phys = physicsBodyNames;
          const missing = phys.filter((n) => !sceneKeys.has(n));
          const extra = Array.from(sceneKeys).filter((k) => !phys.includes(k));
          return (
            <div className="mt-2 grid grid-cols-2 gap-4">
              <div>
                <div className="text-gray-400" style={{ marginBottom: 4 }}>
                  Unmapped physics names
                </div>
                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {missing.join("\n") || "(none)"}
                </pre>
              </div>
              <div>
                <div className="text-gray-400" style={{ marginBottom: 4 }}>
                  Scene keys without physics
                </div>
                <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {extra.join("\n") || "(none)"}
                </pre>
              </div>
            </div>
          );
        } catch {
          return null;
        }
      })()}
    </div>
  );
}
